export const ERROR_SOURCE = {
  ORDER_MANAGER: 'order-manager',
  API_GATEWAY: 'api-gateway',
  NETWORK: 'network',
  CLIENT: 'client'
};

const SOURCE_LABELS = {
  [ERROR_SOURCE.ORDER_MANAGER]: 'Order Manager',
  [ERROR_SOURCE.API_GATEWAY]: 'Galileo API Gateway',
  [ERROR_SOURCE.NETWORK]: 'Network',
  [ERROR_SOURCE.CLIENT]: 'Galileo'
};

const ENTITY_LABELS = {
  order: 'order',
  placement: 'placement',
  pack: 'package',
  advertiser: 'advertiser',
  advertisers: 'advertisers',
  login: 'login',
  page: 'page'
};

const TECHNICAL_PHRASES = [
  /bad gateway/i,
  /gateway time-?out/i,
  /service unavailable/i,
  /internal server error/i,
  /network error/i,
  /econnaborted/i,
  /err_network/i,
  /nginx/i,
  /<\s*html/i
];

const emptyParsed = {
  hasError: false,
  status: 0,
  source: ERROR_SOURCE.CLIENT,
  sourceLabel: SOURCE_LABELS[ERROR_SOURCE.CLIENT],
  title: '',
  message: '',
  hint: '',
  rawMessage: '',
  details: [],
  toastMessage: '',
  isPageBlocking: false
};

function entityLabel(entity) {
  if (!entity) {
    return 'item';
  }
  return ENTITY_LABELS[entity] || entity.replace(/_/g, ' ');
}

function asString(value) {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
}

function extractFromHtml(html) {
  const title = html.match(/<title[^>]*>([^<]+)<\/title>/i);
  const heading = html.match(/<h1[^>]*>([^<]+)<\/h1>/i);
  const text = (title && title[1]) || (heading && heading[1]) || '';
  return text.replace(/\s+/g, ' ').trim();
}

function formatOneError(item) {
  if (item == null) {
    return '';
  }
  if (typeof item === 'string') {
    return item;
  }
  return item.message || item.msg || item.detail || item.error || asString(item);
}

function extractBody(data) {
  if (data == null || data === '') {
    return { message: '', details: [], isHtml: false };
  }

  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed.charAt(0) === '<' || /<html/i.test(trimmed)) {
      return { message: extractFromHtml(trimmed), details: [], isHtml: true };
    }
    try {
      return extractBody(JSON.parse(trimmed));
    } catch (e) {
      return { message: trimmed.slice(0, 500), details: [], isHtml: false };
    }
  }

  if (typeof data !== 'object') {
    return { message: asString(data), details: [], isHtml: false };
  }

  const details = [];
  let message = '';

  if (typeof data.error === 'string') {
    message = data.error;
  } else if (data.error && typeof data.error === 'object' && !Array.isArray(data.error)) {
    message = data.error.message || data.error.msg || data.error.detail || '';
    if (data.error.details) {
      details.push(...normalizeDetails(data.error.details));
    }
  } else if (Array.isArray(data.error)) {
    details.push(...data.error.map(formatOneError).filter(Boolean));
  }

  if (!message && typeof data.message === 'string') {
    message = data.message;
  } else if (!message && data.message && typeof data.message === 'object') {
    message = data.message.message || asString(data.message);
  }

  if (!message && typeof data.msg === 'string') {
    message = data.msg;
  }
  if (!message && typeof data.detail === 'string') {
    message = data.detail;
  }
  if (!message && typeof data.description === 'string') {
    message = data.description;
  }

  if (Array.isArray(data.errors)) {
    details.push(...data.errors.map(formatOneError).filter(Boolean));
  }
  if (Array.isArray(data.details)) {
    details.push(...normalizeDetails(data.details));
  }

  if (!message && details.length > 0) {
    message = details[0];
  }

  return { message: (message || '').trim(), details, isHtml: false };
}

function normalizeDetails(details) {
  if (Array.isArray(details)) {
    return details.map(formatOneError).filter(Boolean);
  }
  const text = formatOneError(details);
  return text ? [text] : [];
}

function headerMap(headers) {
  if (!headers) {
    return {};
  }
  const mapped = {};
  Object.keys(headers).forEach((key) => {
    mapped[key.toLowerCase()] = headers[key];
  });
  return mapped;
}

function detectSource(status, extracted, headers) {
  const headerNames = Object.keys(headers).join(' ').toLowerCase();
  const server = String(headers.server || '').toLowerCase();

  if (extracted.isHtml) {
    return ERROR_SOURCE.API_GATEWAY;
  }
  if (status === 502 || status === 503 || status === 504) {
    return ERROR_SOURCE.API_GATEWAY;
  }
  if (/nginx|kong|envoy|awselb|amazon/.test(server)) {
    return ERROR_SOURCE.API_GATEWAY;
  }
  if (/x-amzn-|x-kong-|x-amz-apigw|x-envoy/.test(headerNames)) {
    return ERROR_SOURCE.API_GATEWAY;
  }
  if (status >= 400) {
    return ERROR_SOURCE.ORDER_MANAGER;
  }
  return ERROR_SOURCE.CLIENT;
}

function titleFor(status, source, entity) {
  const noun = entityLabel(entity);
  if (status === 404) {
    return `We couldn't find this ${noun}`;
  }
  if (status === 408 || (source === ERROR_SOURCE.NETWORK && status === 408)) {
    return 'This is taking too long';
  }
  if (source === ERROR_SOURCE.NETWORK) {
    return "We can't connect to Galileo";
  }
  if (source === ERROR_SOURCE.API_GATEWAY) {
    return 'Galileo is temporarily unavailable';
  }
  if (status === 403) {
    return "You don't have permission to do this";
  }
  if (status === 409) {
    return "This change couldn't be saved";
  }
  if (status === 413) {
    return 'This upload is too large';
  }
  if (status === 422 || status === 400) {
    return "We couldn't save your changes";
  }
  if (status === 429) {
    return 'Please wait a moment and try again';
  }
  if (status >= 500) {
    return 'Something went wrong on our side';
  }
  return 'Something went wrong';
}

function userMessageFor(status, source) {
  if (status === 404) {
    return 'It may have been deleted, or the link you used may be out of date.';
  }
  if (status === 408) {
    return "The server didn't respond in time. Please try again in a moment.";
  }
  if (source === ERROR_SOURCE.NETWORK) {
    return 'Check your internet connection, then try again.';
  }
  if (source === ERROR_SOURCE.API_GATEWAY) {
    return 'This is usually temporary. Wait a few seconds and try again, or reload the page.';
  }
  if (status === 403) {
    return 'If you need access, ask a teammate who can edit this, or email mta-support@hearst.com.';
  }
  if (status === 409) {
    return 'Someone may have changed this already. Reload the page and try again.';
  }
  if (status === 413) {
    return 'Try a smaller file, or split the upload into smaller parts.';
  }
  if (status === 422 || status === 400) {
    return 'Please check what you entered and try again.';
  }
  if (status === 429) {
    return 'Galileo is busy right now. Wait a moment, then try again.';
  }
  if (status >= 500) {
    return 'Your latest change may not have been saved. Please try again. If it keeps happening, email mta-support@hearst.com.';
  }
  return 'Please try again. If it keeps happening, email mta-support@hearst.com.';
}

function isTechnicalPhrase(text) {
  if (!text) {
    return true;
  }
  return TECHNICAL_PHRASES.some((pattern) => pattern.test(text));
}

function humanizeServerMessage(text) {
  if (!text || isTechnicalPhrase(text)) {
    return '';
  }
  let readable = text
    .replace(/[_./]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, (_, first, second) => `${first} ${second.toLowerCase()}`)
    .replace(/\s+/g, ' ')
    .trim();
  if (!readable) {
    return '';
  }
  readable = readable.charAt(0).toUpperCase() + readable.slice(1);
  if (readable.length > 180) {
    readable = `${readable.slice(0, 177)}...`;
  }
  return readable;
}

function isPageBlocking(status, source) {
  if (status === 404) {
    return true;
  }
  if (source === ERROR_SOURCE.NETWORK) {
    return true;
  }
  if (source === ERROR_SOURCE.API_GATEWAY && status >= 500) {
    return true;
  }
  return false;
}

function buildToastMessage(title, message) {
  if (!message || title.indexOf(message) !== -1) {
    return title;
  }
  return `${title}. ${message}`;
}

function parsedResult({
  status,
  source,
  entity,
  rawMessage,
  details,
  isPageBlocking: blocking
}) {
  const sourceLabel = SOURCE_LABELS[source] || SOURCE_LABELS[ERROR_SOURCE.CLIENT];
  const title = titleFor(status, source, entity);
  const message = userMessageFor(status, source);
  const hint = humanizeServerMessage(rawMessage);

  return {
    hasError: true,
    status,
    source,
    sourceLabel,
    title,
    message,
    hint: hint && hint !== title && hint !== message ? hint : '',
    rawMessage: rawMessage || '',
    details: details || [],
    toastMessage: buildToastMessage(title, message),
    isPageBlocking: blocking
  };
}

export function parseApiError(error, entity) {
  if (!error) {
    return emptyParsed;
  }

  if (typeof error === 'string') {
    return parsedResult({
      status: 0,
      source: ERROR_SOURCE.CLIENT,
      entity,
      rawMessage: error,
      details: [],
      isPageBlocking: false
    });
  }

  const response = error.response;
  const status = (response && response.status) || error.status || 0;
  const code = error.code || '';
  const axiosMessage = error.message || '';
  const isTimeout = code === 'ECONNABORTED' || /timeout/i.test(axiosMessage);
  const isNetwork = !response && (
    axiosMessage === 'Network Error' ||
    status === 590 ||
    code === 'ERR_NETWORK' ||
    code === 'ERR_CONNECTION_REFUSED'
  );

  if (isTimeout || isNetwork) {
    const resolvedStatus = isTimeout ? 408 : 590;
    return parsedResult({
      status: resolvedStatus,
      source: ERROR_SOURCE.NETWORK,
      entity,
      rawMessage: axiosMessage || code,
      details: [],
      isPageBlocking: true
    });
  }

  const data = response ? response.data : error.data;
  const headers = headerMap(response && response.headers);
  const extracted = extractBody(data);
  const source = detectSource(status, extracted, headers);
  const statusText = (response && response.statusText) || '';
  const rawMessage = extracted.message || statusText || axiosMessage;
  const details = extracted.details.filter((item) => item && item !== extracted.message);

  return parsedResult({
    status,
    source,
    entity,
    rawMessage,
    details,
    isPageBlocking: isPageBlocking(status, source)
  });
}

export function getErrorSourceLabel(source) {
  return SOURCE_LABELS[source] || SOURCE_LABELS[ERROR_SOURCE.CLIENT];
}
